import { useRouter } from "next/router"
import { Button, Container, Stack, Typography } from "@octopus-energy/coral/atoms"
import { useCoralBreakpoints } from "@octopus-energy/coral/hooks"
import { format } from "date-fns"
import { getSbLinkHref } from "@krakentech/blueprint-utils/storyblok"
import CustomHead from "@/components/atoms/CustomHead"
import { Page } from "@/components/organisms"
import { type SbBlogPostStory } from "../../../../types"
import { StoryblokBlokLooper } from "../../../StoryblokBlokLooper/StoryblokBlokLooper"
import { StoryblokImage } from "../../../StoryblokImage"

type BlogPostTemplateProps = {
    story: SbBlogPostStory
}

// Returns the url of the ROOT blog page
function useBlogBackBtnUrl() {
    const router = useRouter()
    const path = router.pathname.split("/")

    return path.length > 2 ? `/${path[1]}` : null
}

export function BlogPostTemplate({ story }: BlogPostTemplateProps) {
    const backUrl = useBlogBackBtnUrl()

    const { isMaxMd } = useCoralBreakpoints()

    const { author, image, title, intro, blocks, metadata, metaRobots, imageLink } = story.content
    const publicationDate = story.first_published_at
        ? format(new Date(story.first_published_at), "dd MMMM yyyy")
        : null
    const imgHref = imageLink && getSbLinkHref(imageLink)

    return (
        <>
            <CustomHead
                title={metadata?.title}
                description={metadata?.description}
                robots={metaRobots?.join(",")}
            />
            {image && image.filename && (
                <StoryblokImage
                    alt={image.alt}
                    src={image.filename}
                    href={imgHref}
                    target={imageLink?.target}
                    height={isMaxMd ? 200 : 400}
                    fill
                    style={{
                        objectPosition: "center",
                        objectFit: "cover",
                    }}
                />
            )}
            <Page theme="base900">
                <Stack
                    flexDirection="column"
                    gap="xxl"
                >
                    <Typography
                        textStyle="homepageTitle"
                        color="tertiary500"
                        component="h1"
                    >
                        {title}
                    </Typography>
                    <Container
                        paddingX="xxl"
                        paddingY="md"
                        theme="base900"
                    >
                        <Typography
                            textStyle="h4"
                            component="p"
                        >
                            <i>{intro}</i>
                        </Typography>
                    </Container>
                </Stack>
            </Page>
            <StoryblokBlokLooper
                required
                blocks={blocks}
            />
            <Page>
                <Stack
                    justifyContent="space-between"
                    gap="md"
                    flexDirection={{ base: "column", md: "row" }}
                    flexWrap="nowrap"
                >
                    <div>
                        <Typography>
                            <b>{author?.content?.display_name}</b>
                        </Typography>
                        <Typography>{author?.content?.role}</Typography>
                        <Typography textStyle="body2">Publié le {publicationDate}</Typography>
                    </div>
                    {!!backUrl && <Button href={backUrl}>Retour au blog</Button>}
                </Stack>
            </Page>
        </>
    )
}
